/* Tela 5 · Notas emitidas (UC03): chips por situação com contagem, empresa e busca;
   ações por linha conforme o status; gaveta com o DANFSe e o histórico; cancelamento
   com motivo e justificativa (UC05). */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  CloseButton,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import {
  chakraComponents,
  GroupBase,
  Select,
  SelectComponentsConfig,
  ValueContainerProps,
} from 'chakra-react-select';
import {
  ArrowSquareOut,
  Code,
  CopySimple,
  DownloadSimple,
  EnvelopeSimple,
  FileText,
  ListBullets,
  MagnifyingGlass,
  Plus,
  Prohibit,
} from 'phosphor-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useNfseEmissao } from '../../../../../hooks/useNfseEmissao';
import { useAuth } from '../../../../../providers/auth';
import { INfseEmissao, INfseEmitente, NfseEmissaoStatus } from '../../../../../types/nfseEmissao';
import { propsMenuSelect } from '../../../../../utils/selectMenu';
import { CancelarModal } from '../nota/components/CancelarModal';
import {
  editarPath,
  NFSE_ACOES,
  NFSE_EMISSAO_PATH,
  NFSE_PATHS,
  notaPath,
} from '../shared/constants';
import { DanfsePreview, Historico, useAcoesNota } from '../shared/notaDetalhe';
import { Carregando, NfsePage } from '../shared/NfsePage';
import { Totalizadores } from './Totalizadores';
import { Linha, SubLinha, Tabela, Td, Th, Vazio } from '../shared/tabela';
import {
  brl,
  C,
  Card,
  Chip,
  cnpjMask,
  dataBR,
  EnvBadge,
  ModuleHeader,
  MONO,
  NButton,
  NfseDrawer,
  NIconButton,
  StatusTag,
} from '../shared/ui';

const FILTROS: [NfseEmissaoStatus | 'todas', string][] = [
  ['todas', 'Todas'],
  ['autorizada', 'Autorizadas'],
  ['rejeitada', 'Rejeitadas'],
  ['rascunho', 'Rascunhos'],
  ['cancelada', 'Canceladas'],
  ['substituida', 'Substituídas'],
];

const TAMANHO = 20;
const COM_DOCUMENTO = ['autorizada', 'cancelada', 'substituida'];

// Visual comum aos dois campos de busca da barra de filtros.
const CAMPO_BUSCA = {
  h: '38px',
  pl: '38px',
  // 16px no celular: abaixo disso o iOS dá zoom na página ao focar o campo.
  fontSize: { base: '16px', md: '13.5px' },
  borderRadius: '999px',
  bg: 'rgba(255, 255, 255, 0.9)',
  borderColor: C.lineInput,
  _placeholder: { color: C.subtle },
};
const CAMPO_HOVER = { borderColor: C.subtle };
const CAMPO_FOCO = { borderColor: C.accent, boxShadow: `0 0 0 3px ${C.brand100}`, bg: 'white' };

interface OpcaoEmitente {
  value: number;
  label: string;
  cnpj: string;
  codigo: string;
  // Nome sem acento e em minúsculas, calculado uma vez por lista.
  chave: string;
}

const ACENTOS = /[̀-ͯ]/g;
const normalizar = (s: string) => s.normalize('NFD').replace(ACENTOS, '').toLowerCase();

/* Busca da empresa: sem diferenciar maiúscula/minúscula nem acento, por qualquer pedaço
   do nome ("moda" acha "...UNIDADE MODA LTDA"), por palavras em qualquer ordem
   ("moda especialistas"), pelo código (external_id) ou pelo CNPJ com ou sem pontuação.
   Ordem: código exato, código que começa com o termo, nome que começa com o termo,
   resto em ordem alfabética. */
function filtrarEmitentes(opcoes: OpcaoEmitente[], termo: string): OpcaoEmitente[] {
  const palavras = normalizar(termo).split(/\s+/).filter(Boolean);
  if (!palavras.length) return opcoes;
  const texto = palavras.join(' ');
  const digitos = termo.replace(/\D/g, '');
  const soNumero = /^[\d\s./-]+$/.test(termo.trim());
  const peso = (o: OpcaoEmitente) => {
    if (o.codigo === texto) return 0;
    if (o.codigo.startsWith(texto)) return 1;
    if (o.chave.startsWith(texto)) return 2;
    return 3;
  };
  return opcoes
    .filter(o => {
      if (soNumero && digitos && (o.codigo.includes(digitos) || o.cnpj.includes(digitos)))
        return true;
      const alvo = `${o.chave} ${o.codigo}`;
      return palavras.every(p => alvo.includes(p));
    })
    .sort((a, b) => peso(a) - peso(b) || a.label.localeCompare(b.label));
}

function ValueContainerComLupa(props: ValueContainerProps<OpcaoEmitente, false>) {
  const { children } = props;
  return (
    <chakraComponents.ValueContainer {...props}>
      <Box position="absolute" left="13px" display="flex" color={C.subtle}>
        <MagnifyingGlass size={16} />
      </Box>
      {children}
    </chakraComponents.ValueContainer>
  );
}

// Fora do componente: definido inline, o react-select remontaria o campo a cada render.
const COMPONENTES_BUSCA: SelectComponentsConfig<OpcaoEmitente, false, GroupBase<OpcaoEmitente>> = {
  DropdownIndicator: null,
  IndicatorSeparator: null,
  ValueContainer: ValueContainerComLupa,
};

// Filtro de empresa como campo de busca: digita nome ou CNPJ e escolhe numa lista de
// sugestões. Sem seta de combobox; o × limpa e volta para todas as empresas.
function BuscaEmitente({
  emitentes,
  valor,
  onEscolher,
}: {
  emitentes: INfseEmitente[];
  valor: number | null;
  onEscolher: (id: number | null) => void;
}) {
  const [termo, setTermo] = useState('');
  const opcoes = useMemo<OpcaoEmitente[]>(
    () =>
      emitentes.map(e => ({
        value: e.id,
        label: e.razao_social,
        cnpj: (e.cnpj ?? '').replace(/\D/g, ''),
        codigo: (e.external_id ?? '').trim().toLowerCase(),
        chave: normalizar(e.razao_social ?? ''),
      })),
    [emitentes],
  );
  const filtradas = useMemo(() => filtrarEmitentes(opcoes, termo), [opcoes, termo]);
  return (
    <Select<OpcaoEmitente>
      aria-label="Buscar empresa"
      placeholder="Empresa, código ou CNPJ"
      // A filtragem e a ordem são nossas (filtrarEmitentes); o react-select só exibe.
      options={filtradas}
      filterOption={null}
      inputValue={termo}
      onInputChange={(v, { action }) => {
        if (action === 'input-change') setTermo(v);
        if (action === 'menu-close' || action === 'set-value') setTermo('');
      }}
      value={opcoes.find(o => o.value === valor) ?? null}
      onChange={o => onEscolher(o?.value ?? null)}
      isClearable
      openMenuOnClick={false}
      formatOptionLabel={(o, { context }) =>
        context === 'menu' ? (
          <Flex gap={2} align="baseline" justify="space-between">
            <Box minW={0}>
              <Text fontSize="13px" noOfLines={1}>
                {o.label}
              </Text>
              <Text fontSize="11px" color={C.subtle} fontFamily={MONO}>
                {cnpjMask(o.cnpj)}
              </Text>
            </Box>
            {o.codigo && (
              <Text fontSize="11px" color={C.subtle} fontFamily={MONO} flex="none">
                cód. {o.codigo}
              </Text>
            )}
          </Flex>
        ) : (
          <Text as="span" noOfLines={1}>
            {o.codigo ? `${o.codigo} · ` : ''}
            {o.label}
          </Text>
        )
      }
      noOptionsMessage={() => 'Nenhuma empresa encontrada'}
      components={COMPONENTES_BUSCA}
      chakraStyles={{
        control: base => ({
          ...base,
          minH: CAMPO_BUSCA.h,
          h: CAMPO_BUSCA.h,
          fontSize: CAMPO_BUSCA.fontSize,
          borderRadius: CAMPO_BUSCA.borderRadius,
          bg: CAMPO_BUSCA.bg,
          borderColor: CAMPO_BUSCA.borderColor,
          _hover: CAMPO_HOVER,
          _focusWithin: CAMPO_FOCO,
        }),
        valueContainer: base => ({ ...base, pl: CAMPO_BUSCA.pl, pr: 1 }),
        placeholder: base => ({ ...base, color: C.subtle }),
        singleValue: base => ({ ...base, fontWeight: 600 }),
        clearIndicator: base => ({ ...base, mr: 1 }),
      }}
      menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
      styles={{ menuPortal: base => ({ ...base, zIndex: 2000 }) }}
      {...propsMenuSelect}
    />
  );
}

function useAtrasado<T>(valor: T, ms = 300) {
  const [v, setV] = useState(valor);
  useEffect(() => {
    const t = setTimeout(() => setV(valor), ms);
    return () => clearTimeout(t);
  }, [valor, ms]);
  return v;
}

export function NfseEmissaoNotasPage() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { getEmissoes, getEmitentes, getContagem, getTotalizadores, getPainel } = useNfseEmissao();
  const acoes = useAcoesNota();
  // Filtros e página moram na URL: quem abre uma rejeitada para corrigir e volta (ou
  // compartilha o link) encontra a lista do jeito que deixou.
  const [params, setParams] = useSearchParams();
  const statusUrl = params.get('status');
  const filtro = FILTROS.some(([chave]) => chave === statusUrl)
    ? (statusUrl as NfseEmissaoStatus)
    : 'todas';
  const emitenteId = Number(params.get('empresa')) || null;
  const page = Number(params.get('pagina')) || 1;
  const buscaUrl = params.get('busca') ?? '';
  const [busca, setBusca] = useState(buscaUrl);
  const [detalhe, setDetalhe] = useState<INfseEmissao | null>(null);
  const tomador = useAtrasado(busca.trim());

  const podeEmitir = hasPermission(NFSE_EMISSAO_PATH, NFSE_ACOES.emitir);
  const podeCancelar = hasPermission(NFSE_EMISSAO_PATH, NFSE_ACOES.cancelar);

  // Valor vazio, "todas" ou página 1 saem da URL. Mudar filtro volta para a página 1.
  const mudarUrl = (mudancas: Record<string, string | number | null>) =>
    setParams(
      atual => {
        const novo = new URLSearchParams(atual);
        Object.entries(mudancas).forEach(([k, v]) => {
          if (v == null || v === '' || v === 'todas' || (k === 'pagina' && v === 1)) novo.delete(k);
          else novo.set(k, String(v));
        });
        return novo;
      },
      { replace: true },
    );
  const setFiltro = (v: NfseEmissaoStatus | 'todas') => mudarUrl({ status: v, pagina: null });
  const setEmitenteId = (v: number | null) => mudarUrl({ empresa: v, pagina: null });
  const setPage = (v: number) => mudarUrl({ pagina: v });
  const temFiltro = filtro !== 'todas' || !!emitenteId || !!busca.trim();
  const limparFiltros = () => {
    setBusca('');
    mudarUrl({ status: null, empresa: null, busca: null, pagina: null });
  };

  useEffect(() => {
    if (tomador !== buscaUrl) mudarUrl({ busca: tomador, pagina: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tomador]);

  const comuns = { emitente_id: emitenteId ?? undefined, tomador: tomador || undefined };
  const emissoesQuery = useQuery({
    queryKey: [
      'nfse-emissoes',
      { ...comuns, status: filtro === 'todas' ? undefined : filtro, page, size: TAMANHO },
    ],
    queryFn: getEmissoes,
    placeholderData: prev => prev,
  });
  const contagem = useQuery({ queryKey: ['nfse-contagem', comuns], queryFn: getContagem }).data;
  const totalizadores = useQuery({
    queryKey: ['nfse-totalizadores', comuns],
    queryFn: getTotalizadores,
    placeholderData: prev => prev,
  }).data;
  const emitentes = useQuery({ queryKey: ['nfse-emitentes'], queryFn: getEmitentes }).data ?? [];
  // Feedback enquanto o debounce ou a consulta da busca ainda não terminou.
  const buscando = busca.trim() !== tomador || (!!tomador && emissoesQuery.isFetching);
  const ambiente = useQuery({ queryKey: ['nfse-painel', 'mes'], queryFn: getPainel }).data
    ?.ambiente;

  const pagina = emissoesQuery.data?.data;
  const notas = pagina?.items ?? [];
  const total = contagem ? Object.values(contagem).reduce((a, n) => a + n, 0) : undefined;
  // Trocou chip/empresa/página e a resposta não chegou: a tabela ainda é a do filtro
  // anterior. Esmaecer evita ler (e agir sobre) linhas que vão sumir.
  const desatualizada = emissoesQuery.isPlaceholderData;
  // Com uma empresa escolhida a coluna Emitente repetiria o mesmo nome em toda linha.
  const mostrarEmitente = !emitenteId;
  const colunas = mostrarEmitente ? 8 : 7;

  const abrirLinha = (n: INfseEmissao) => {
    if (n.status === 'rejeitada' || n.status === 'rascunho') navigate(editarPath(n.id));
    else setDetalhe(n);
  };
  const parar = (fn: () => void) => (ev: React.MouseEvent) => {
    ev.stopPropagation();
    fn();
  };

  return (
    <NfsePage wide ambiente={ambiente}>
      <ModuleHeader
        title="Notas emitidas"
        icon={ListBullets}
        tone="green"
        description="Tudo o que saiu pela tela, com rascunhos e rejeições. Clique numa linha para ver o DANFSe e o histórico."
        crumbs={[{ label: 'Emissão de NFS-e', to: NFSE_PATHS.painel }, { label: 'Notas emitidas' }]}
        actions={
          <>
            <EnvBadge ambiente={ambiente} />
            {podeEmitir && (
              <NButton icon={Plus} onClick={() => navigate(NFSE_PATHS.emitir)}>
                Nova NFS-e
              </NButton>
            )}
          </>
        }
      />

      <Totalizadores dados={totalizadores} />

      <Card overflow="hidden">
        <Flex
          p={{ base: '12px', md: '16px 20px' }}
          gap={3}
          align="center"
          wrap="wrap"
          borderBottomWidth="1px"
          borderColor={C.line}
        >
          {/* No celular os chips ficam numa linha só, com rolagem lateral, em vez de
              empilhar três linhas antes dos campos. */}
          <Flex
            gap="5px"
            wrap={{ base: 'nowrap', md: 'wrap' }}
            overflowX={{ base: 'auto', md: 'visible' }}
            w={{ base: 'full', md: 'auto' }}
            mx={{ base: '-12px', md: 0 }}
            px={{ base: '12px', md: 0 }}
            pb={{ base: '2px', md: 0 }}
            sx={{
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
              '& > *': { flexShrink: 0 },
            }}
          >
            {FILTROS.map(([chave, rotulo]) => (
              <Chip
                key={chave}
                selected={filtro === chave}
                count={chave === 'todas' ? total : contagem?.[chave]}
                onClick={() => setFiltro(chave)}
              >
                {rotulo}
              </Chip>
            ))}
          </Flex>
          {/* Celular: um campo por linha, largura toda. A partir de sm: lado a lado. */}
          <Flex
            gap={2}
            flex="1 1 520px"
            justify="flex-end"
            direction={{ base: 'column', sm: 'row' }}
            w={{ base: 'full', lg: 'auto' }}
          >
            <Box flex={{ sm: '1 1 0' }} minW={0} maxW={{ base: 'full', md: '300px' }}>
              <BuscaEmitente emitentes={emitentes} valor={emitenteId} onEscolher={setEmitenteId} />
            </Box>
            <InputGroup flex={{ sm: '1 1 0' }} minW={0} maxW={{ base: 'full', md: '300px' }}>
              <InputLeftElement pointerEvents="none" h="38px" w="40px">
                {buscando ? (
                  <Spinner size="xs" color={C.subtle} />
                ) : (
                  <MagnifyingGlass size={16} color={C.subtle} />
                )}
              </InputLeftElement>
              <Input
                aria-label="Buscar notas"
                placeholder="Tomador, CNPJ ou nº da nota"
                value={busca}
                onChange={e => setBusca(e.target.value)}
                onKeyDown={e => e.key === 'Escape' && setBusca('')}
                {...CAMPO_BUSCA}
                _hover={CAMPO_HOVER}
                _focusVisible={CAMPO_FOCO}
                pr={busca ? '36px' : '14px'}
              />
              {busca && (
                <InputRightElement h="38px" w="36px">
                  <CloseButton size="sm" aria-label="Limpar busca" onClick={() => setBusca('')} />
                </InputRightElement>
              )}
            </InputGroup>
          </Flex>
        </Flex>

        {emissoesQuery.isLoading ? (
          <Carregando />
        ) : (
          <Box
            opacity={desatualizada ? 0.55 : 1}
            transition="opacity 0.15s"
            pointerEvents={desatualizada ? 'none' : undefined}
            aria-busy={desatualizada}
          >
            <Tabela
              cabecalho={
                <>
                  <Th title="Série · número da DPS">DPS</Th>
                  <Th>NFS-e</Th>
                  <Th>Data</Th>
                  {mostrarEmitente && <Th>Emitente</Th>}
                  <Th>Tomador</Th>
                  <Th num>Valor</Th>
                  <Th>Status</Th>
                  <Th textAlign="right">Ações</Th>
                </>
              }
            >
              {notas.map((n, i) => {
                const erro = n.erros?.[0];
                const codigo = erro?.codigo;
                return (
                  <Linha
                    key={n.id}
                    i={i}
                    selecionada={detalhe?.id === n.id}
                    onClick={() => abrirLinha(n)}
                  >
                    <Td fontFamily={MONO} fontSize="12.5px" whiteSpace="nowrap">
                      {n.serie} · {n.n_dps ?? '—'}
                    </Td>
                    <Td fontWeight={700}>{n.n_nfse ?? '—'}</Td>
                    <Td whiteSpace="nowrap">{dataBR(n.dh_processamento ?? n.criado_em)}</Td>
                    {mostrarEmitente && (
                      <Td maxW="200px">
                        <Text noOfLines={1} title={n.emitente_razao_social}>
                          {n.emitente_razao_social}
                        </Text>
                      </Td>
                    )}
                    <Td maxW="260px">
                      <Text fontWeight={600} noOfLines={1}>
                        {n.tomador_nome ?? '—'}
                      </Text>
                      <SubLinha>
                        {n.tomador_doc ? cnpjMask(n.tomador_doc) : 'sem documento'}
                      </SubLinha>
                    </Td>
                    <Td num whiteSpace="nowrap">
                      {brl(n.v_serv)}
                    </Td>
                    <Td>
                      <StatusTag kind={n.status}>
                        {n.status === 'rejeitada' && codigo ? `Rejeitada · ${codigo}` : undefined}
                      </StatusTag>
                      {/* O motivo à vista: dá para triar as rejeições sem abrir uma por uma. */}
                      {n.status === 'rejeitada' && erro?.descricao && (
                        <Text
                          fontSize="12px"
                          color={C.dangerText}
                          mt={0.5}
                          maxW="260px"
                          noOfLines={1}
                          title={[erro.descricao, erro.complemento].filter(Boolean).join(' — ')}
                        >
                          {erro.descricao}
                        </Text>
                      )}
                      {n.substitui_n_nfse && (
                        <SubLinha>substitui a nº {n.substitui_n_nfse}</SubLinha>
                      )}
                    </Td>
                    <Td>
                      <Flex gap={1} justify="flex-end" align="center">
                        {n.status === 'rejeitada' && podeEmitir && (
                          <NButton
                            size="sm"
                            variant="secondary"
                            onClick={parar(() => navigate(editarPath(n.id)))}
                          >
                            Corrigir e reenviar
                          </NButton>
                        )}
                        {n.status === 'rascunho' && podeEmitir && (
                          <NButton
                            size="sm"
                            variant="secondary"
                            onClick={parar(() => navigate(editarPath(n.id)))}
                          >
                            Continuar
                          </NButton>
                        )}
                        {COM_DOCUMENTO.includes(n.status) && (
                          <>
                            <NIconButton
                              icon={DownloadSimple}
                              label="Baixar PDF"
                              onClick={parar(() => acoes.baixar(n, 'pdf'))}
                            />
                            <NIconButton
                              icon={Code}
                              label="Baixar XML"
                              onClick={parar(() => acoes.baixar(n, 'xml'))}
                            />
                          </>
                        )}
                        {n.status === 'autorizada' && (
                          <>
                            <NIconButton
                              icon={EnvelopeSimple}
                              label="Enviar por e-mail"
                              onClick={parar(() => navigate(notaPath(n.id)))}
                            />
                            {podeEmitir && (
                              <NIconButton
                                icon={CopySimple}
                                label="Duplicar"
                                onClick={parar(() => acoes.duplicar(n))}
                              />
                            )}
                            {podeCancelar && (
                              <NIconButton
                                icon={Prohibit}
                                label="Cancelar"
                                onClick={parar(() => acoes.abrirCancelamento(n))}
                              />
                            )}
                          </>
                        )}
                      </Flex>
                    </Td>
                  </Linha>
                );
              })}
              {notas.length === 0 && (
                <Vazio colunas={colunas}>
                  {temFiltro ? (
                    <Flex direction="column" align="center" gap={3}>
                      <Text>
                        {tomador
                          ? `Nenhuma nota encontrada para “${tomador}”.`
                          : 'Nenhuma nota com esses filtros.'}
                      </Text>
                      <NButton size="sm" variant="secondary" onClick={limparFiltros}>
                        Limpar filtros
                      </NButton>
                    </Flex>
                  ) : (
                    'Nenhuma nota emitida ainda.'
                  )}
                </Vazio>
              )}
            </Tabela>
          </Box>
        )}

        <Flex
          p="12px 20px"
          justify="space-between"
          align="center"
          fontSize="12.5px"
          color={C.subtle}
          wrap="wrap"
          gap={2}
        >
          <Text>{pagina?.total ?? 0} nota(s)</Text>
          <Flex align="center" gap={3}>
            {(pagina?.pages ?? 1) > 1 && (
              <Flex align="center" gap={1}>
                <NButton
                  size="sm"
                  variant="ghost"
                  isDisabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  Anterior
                </NButton>
                <Text fontSize="12px" whiteSpace="nowrap">
                  Página {page} de {pagina?.pages}
                </Text>
                <NButton
                  size="sm"
                  variant="ghost"
                  isDisabled={page >= (pagina?.pages ?? 1)}
                  onClick={() => setPage(page + 1)}
                >
                  Próxima
                </NButton>
              </Flex>
            )}
          </Flex>
        </Flex>
      </Card>

      <NfseDrawer
        isOpen={!!detalhe}
        onClose={() => setDetalhe(null)}
        size="xl"
        icon={FileText}
        tone={detalhe?.status === 'autorizada' ? 'green' : 'blue'}
        title={
          detalhe ? `NFS-e nº ${detalhe.n_nfse ?? '—'} · ${detalhe.emitente_razao_social}` : ''
        }
        subtitle={detalhe ? `${detalhe.tomador_nome ?? '—'} · ${brl(detalhe.v_serv)}` : ''}
        right={detalhe && <StatusTag kind={detalhe.status} />}
        footer={
          detalhe && (
            <>
              {detalhe.status === 'autorizada' && podeCancelar && (
                <NButton
                  variant="ghost"
                  icon={Prohibit}
                  onClick={() => {
                    acoes.abrirCancelamento(detalhe);
                    setDetalhe(null);
                  }}
                >
                  Cancelar
                </NButton>
              )}
              <NButton
                variant="ghost"
                icon={ArrowSquareOut}
                onClick={() => navigate(notaPath(detalhe.id))}
              >
                Abrir nota
              </NButton>
              <NButton
                variant="secondary"
                icon={Code}
                isLoading={acoes.baixando === `${detalhe.id}-xml`}
                onClick={() => acoes.baixar(detalhe, 'xml')}
              >
                XML
              </NButton>
              <NButton
                icon={DownloadSimple}
                isLoading={acoes.baixando === `${detalhe.id}-pdf`}
                onClick={() => acoes.baixar(detalhe, 'pdf')}
              >
                PDF
              </NButton>
            </>
          )
        }
      >
        {detalhe && (
          <Flex gap="18px" direction={{ base: 'column', md: 'row' }}>
            <Box flex={1} minW={0}>
              <DanfsePreview emissao={detalhe} />
            </Box>
            <Box w={{ base: 'full', md: '190px' }} flex="none">
              <Historico emissao={detalhe} />
            </Box>
          </Flex>
        )}
      </NfseDrawer>

      <CancelarModal
        isOpen={!!acoes.cancelando}
        onClose={acoes.fecharCancelamento}
        numero={acoes.cancelando?.n_nfse}
        isLoading={acoes.cancelar.isPending}
        onConfirmar={payload =>
          acoes.cancelando && acoes.cancelar.mutate({ nota: acoes.cancelando, payload })
        }
      />
    </NfsePage>
  );
}
